import { useState } from "react";
import { PlanVersion, PlanChange } from "@features/onboarding/utility-setup/types";
import { usePlanVersion } from "../../../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { GenericTable } from "@shared/components/GenericTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { ArrowRight, Eye, Clock, User, Hash } from "lucide-react";

interface PlanVersionTableProps {
  planId: number;
}

interface ChangesModalProps {
  version: string;
  changes: PlanChange[];
  lastModifiedDate: string;
  lastModifiedBy: string;
}

const ChangesModal: React.FC<ChangesModalProps> = ({ 
  version, 
  changes, 
  lastModifiedDate, 
  lastModifiedBy 
}) => {
  return (
    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
        
           {version} Changes
        </DialogTitle>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {lastModifiedDate}
          </div>
          <div className="flex items-center gap-1">
            <User className="w-4 h-4" />
            {lastModifiedBy}
          </div>
        </div>
      </DialogHeader>
      
      <div className="space-y-4 mt-4">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold">Changes ({changes.length})</h4>
          <Badge variant="secondary">{changes.length} modifications</Badge>
        </div>
        
        {changes.length === 0 ? (
          <Card>
            <CardContent className="flex items-center justify-center py-8">
              <p className="text-gray-500">No changes recorded for this version</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {changes.map((change, index) => (
              <Card key={index} className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-800">
                    {change.field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 mb-1">Previous Value</div>
                      <div className="bg-red-50 border border-red-200 rounded p-2 text-sm">
                        {change.oldValue || <span className="text-gray-400 italic">Empty</span>}
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 mb-1">New Value</div>
                      <div className="bg-green-50 border border-green-200 rounded p-2 text-sm">
                        {change.newValue || <span className="text-gray-400 italic">Empty</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DialogContent>
  );
};

const PlanVersionTable: React.FC<PlanVersionTableProps> = ({ planId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data: planVersions, isLoading } = usePlanVersion({
    planId,
    page: currentPage,
    pageSize: PAGE_SIZE,
  });

  console.log("planVersions", planVersions);

  const tableData = (planVersions?.result || []).map((item, index) => ({
    id: `${item.version}-${index}`,
    version: item.version,
    lastModifiedDate: item.lastModifiedDate,
    lastModifiedBy: item.lastModifiedUserRemoteName || "—",
    changeCount: item.changes?.length ?? 0,
    changes: item.changes || [],
    originalItem: item, // Keep reference to original item for modal
  }));

  console.log("tableData", tableData);

  const pagination = planVersions
    ? {
        currentPage,
        totalPages: Math.ceil(planVersions.count / PAGE_SIZE),
        totalItems: planVersions.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, planVersions.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const columns = [
    {
      key: "version",
      header: "Version",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "lastModifiedDate",
      header: "Date Modified",
      render: (value: string) => (
        <span className="text-sm text-gray-700">{value}</span>
      ),
    },
    {
      key: "lastModifiedBy",
      header: "Modified By",
      render: (value: string) => (
        <span className="text-sm text-gray-700">{value}</span>
      ),
    },
    {
      key: "changeCount",
      header: "Changes",
      render: (value: number, row: any) => {
        if (value === 0) {
          return (
            <span className="text-sm text-gray-400">No Changes</span>
          );
        }

        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1 h-auto font-medium"
              >
                <Eye className="w-4 h-4 mr-1" />
                View Changes ({value})
              </Button>
            </DialogTrigger>
            <ChangesModal
              version={row.version}
              changes={row.changes}
              lastModifiedDate={row.lastModifiedDate}
              lastModifiedBy={row.lastModifiedBy}
            />
          </Dialog>
        );
      },
    },
  ];

  return (
    <div className="w-full space-y-4 pt-6">
      <div className="flex items-center justify-between">
        <h3 className="card-subheading-text">
          Plan Version History ({planVersions?.count || 0})
        </h3>
      </div>
      
      <GenericTable
        data={tableData}
        columns={columns}
        selectedRows={[]}
        onSelectionChange={() => {}}
        pagination={pagination}
        onPageChange={(page) => setCurrentPage(page)}
        emptyMessage="No version history found"
        rowKey="id"
        loading={isLoading}
      />
    </div>
  );
};

export default PlanVersionTable;